import React, { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Bookmark, Search, Trash2, Play, Plus } from "lucide-react";
import { PersistedIntelligenceSearch, formatRelativeTime } from "../Services/IntelligencePersistence";

interface SavedSearchesDrawerProps {
  open: boolean;
  onClose: () => void;
  savedSearches: PersistedIntelligenceSearch[];
  canSaveCurrent: boolean;
  currentTerm: string;
  currentFilterCount: number;
  onApply: (entry: PersistedIntelligenceSearch) => void;
  onDelete: (id: string) => void;
  onSave: (name: string) => void;
}

export const SavedSearchesDrawer: React.FC<SavedSearchesDrawerProps> = ({
  open,
  onClose,
  savedSearches,
  canSaveCurrent,
  currentTerm,
  currentFilterCount,
  onApply,
  onDelete,
  onSave,
}) => {
  const [name, setName] = useState("");

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
    setName("");
  };

  return (
    <Transition show={open} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={React.Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md h-full bg-white shadow-2xl flex flex-col">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white flex items-center justify-center">
                        <Bookmark size={15} />
                      </span>
                      <div>
                        <Dialog.Title className="text-sm font-black text-slate-800">Saved Searches</Dialog.Title>
                        <p className="text-[10px] text-slate-400 font-medium">
                          {savedSearches.length} saved search{savedSearches.length === 1 ? "" : "es"}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                      aria-label="Close"
                    >
                      <span className="text-lg leading-none">×</span>
                    </button>
                  </div>

                  {/* Save current search */}
                  <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/70">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                      Save current search
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSave()}
                        disabled={!canSaveCurrent}
                        placeholder={
                          canSaveCurrent
                            ? currentTerm
                              ? `"${currentTerm.slice(0, 32)}${currentTerm.length > 32 ? "…" : ""}"${currentFilterCount > 0 ? ` + ${currentFilterCount} filter(s)` : ""}`
                              : `${currentFilterCount} filter(s)`
                            : "Nothing to save — add a keyword or filter"
                        }
                        className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 disabled:bg-slate-50"
                      />
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={!canSaveCurrent || !name.trim()}
                        className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-white text-xs font-bold hover:opacity-90 disabled:opacity-40 flex items-center gap-1.5 transition-all"
                      >
                        <Plus size={14} /> Save
                      </button>
                    </div>
                  </div>

                  {/* Saved list */}
                  <div className="flex-1 overflow-y-auto px-5 py-4">
                    {savedSearches.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center py-10">
                        <Bookmark size={26} className="text-slate-200 mb-3" />
                        <p className="text-sm font-bold text-slate-400">No saved searches yet</p>
                        <p className="text-xs text-slate-300 mt-1 max-w-[240px]">
                          Build a search with keywords or advanced filters, then save it here to run it again in one click.
                        </p>
                      </div>
                    ) : (
                      <ul className="space-y-2">
                        {savedSearches.map((entry) => (
                          <li
                            key={entry.id}
                            className="group rounded-xl border border-slate-100 bg-white p-3 hover:border-indigo-200 hover:shadow-sm transition-all"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-xs font-black text-slate-800 truncate">{entry.name || "Untitled"}</p>
                                <p className="text-[10px] text-slate-400 font-medium mt-0.5 flex items-center gap-1 flex-wrap">
                                  {entry.filterCount > 0 && (
                                    <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 font-bold">
                                      {entry.filterCount} filter{entry.filterCount === 1 ? "" : "s"}
                                    </span>
                                  )}
                                  {entry.term && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-semibold">
                                      <Search size={9} /> {entry.term.length > 36 ? `${entry.term.slice(0, 36)}…` : entry.term}
                                    </span>
                                  )}
                                  <span>{formatRelativeTime(entry.createdAt)}</span>
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => onDelete(entry.id)}
                                className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-all"
                                title="Delete saved search"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => onApply(entry)}
                              className="mt-2 w-full px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-[11px] font-bold hover:bg-indigo-700 flex items-center justify-center gap-1.5 transition-colors"
                            >
                              <Play size={12} /> Run this search
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SavedSearchesDrawer;